import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Sparkles, Check, Circle, Plus, X, MessageCircle, Copy, Pencil, Save } from "lucide-react";

const todayStr = () => new Date().toISOString().slice(0,10);

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success("Copied — paste into WhatsApp");
  } catch {
    // Fallback for older browsers
    const ta = document.createElement("textarea");
    ta.value = text; document.body.appendChild(ta); ta.select();
    document.execCommand("copy"); document.body.removeChild(ta);
    toast.success("Copied");
  }
};

export default function Housekeeping() {
  const [checklist, setChecklist] = useState([]);
  const [extras, setExtras] = useState([]);
  const [log, setLog] = useState(null);
  const [logDate, setLogDate] = useState(todayStr());
  const [accs, setAccs] = useState([]);
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [addingTask, setAddingTask] = useState("");
  const [waMessages, setWaMessages] = useState([]);
  const [editingKey, setEditingKey] = useState(null);
  const [draftTemplate, setDraftTemplate] = useState("");

  // Load accommodations once so we can offer country/city filters. Housekeeping
  // logs are keyed per destination — matches how Aii Group actually runs the
  // programme across India, Nepal, and beyond.
  useEffect(() => {
    api.get("/accommodations").then(({ data }) => {
      setAccs(data || []);
      if (data?.length && !country) {
        setCountry(data[0].country);
        setCity(data[0].city);
      }
    }).catch(() => {});
  }, [country]);

  const countries = Array.from(new Set(accs.map(a => a.country))).sort();
  const cities = Array.from(new Set(accs.filter(a => a.country === country).map(a => a.city))).sort();

  const load = async () => {
    const [c, l, ex, wa] = await Promise.all([
      api.get("/housekeeping/checklist"),
      api.get(`/housekeeping/logs/${logDate}`, { params: { country, city } }),
      api.get("/housekeeping_extras").catch(() => ({ data: [] })),
      api.get("/housekeeping/wa-templates", { params: country ? { country } : {} }).catch(() => ({ data: [] })),
    ]);
    setChecklist(c.data.items);
    setExtras(ex.data);
    setWaMessages(wa.data);
    setLog(l.data || { log_date: logDate, country, city, items: {}, cook_absent: false, cook_absent_meals: "", budget_requested: 0 });
  };
  useEffect(() => { load(); }, [logDate, country, city]);

  const saveTemplate = async (key, saveScope) => {
    // saveScope: "global" writes to country="", "local" writes to the currently selected country.
    const targetCountry = saveScope === "local" ? country : "";
    try {
      await api.put(`/housekeeping/wa-templates/${key}`, { key, template: draftTemplate, country: targetCountry });
      toast.success(targetCountry ? `Localised for ${targetCountry}` : "Saved as global default");
      setEditingKey(null);
      setDraftTemplate("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const allItems = [...checklist, ...extras.map(x => x.name)];

  const toggle = (label) => {
    setLog(v => ({ ...v, items: { ...v.items, [label]: !v.items[label] } }));
  };
  const save = async () => {
    await api.post("/housekeeping/logs", {
      log_date: logDate, country, city, items: log.items,
      cook_absent: !!log.cook_absent, cook_absent_meals: log.cook_absent_meals || "",
      budget_requested: parseFloat(log.budget_requested) || 0,
    });
    toast.success(`Housekeeping log saved for ${city}, ${country}`);
  };

  const done = allItems.filter(x => log?.items?.[x]).length;

  const addTask = async () => {
    if (!addingTask.trim()) return;
    await api.post("/housekeeping_extras", { name: addingTask.trim(), extra: "" });
    setAddingTask("");
    await load();
    toast.success("Task added");
  };
  const rmExtra = async (id) => { await api.delete(`/housekeeping_extras/${id}`); await load(); };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2"><Sparkles className="text-brand"/><h1 className="text-2xl font-semibold">Housekeeping</h1></div>
        <div className="flex items-center gap-2 flex-wrap">
          <select data-testid="hk-country" value={country} onChange={e => { setCountry(e.target.value); setCity(""); }}
            className="px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm">
            <option value="">— Country —</option>
            {countries.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select data-testid="hk-city" value={city} onChange={e => setCity(e.target.value)}
            className="px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm" disabled={!country}>
            <option value="">— City —</option>
            {cities.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <input type="date" value={logDate} onChange={e => setLogDate(e.target.value)} className="px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/>
          <button onClick={save} className="bg-brand text-white font-semibold px-4 py-2 rounded-md hover:bg-brand-dark" data-testid="hk-save">Save log</button>
        </div>
      </div>
      {(!country || !city) && (
        <div className="rounded-md bg-amber-50 border border-amber-200 text-amber-900 px-4 py-2 text-xs" data-testid="hk-pick-dest">
          Pick a country + city to log housekeeping for that destination. Set up destinations first from <b>Accommodation</b>.
        </div>
      )}

      <div className="aii-card p-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-semibold flex items-center gap-2"><MessageCircle size={16} className="text-brand" /> WhatsApp messages — daily check-ins</h3>
            <p className="text-xs text-[color:var(--aii-text-2)] mt-1">Copy the template, send it on WhatsApp to today&apos;s guests, then tick it off. Click the pencil to edit any template.</p>
          </div>
          <div className="text-sm text-[color:var(--aii-text-2)]">
            {waMessages.filter(m => log?.items?.[`wa_${m.key}`]).length}/{waMessages.length} sent
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {waMessages.map(m => {
            const key = `wa_${m.key}`;
            const on = !!log?.items?.[key];
            const isEditing = editingKey === m.key;
            return (
              <div key={m.key} data-testid={`wa-msg-${m.key}`} className={`border rounded-lg p-3 ${on ? "border-brand bg-[color:var(--aii-green-tint)]" : "border-[color:var(--aii-border)] bg-white"}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <button onClick={()=>toggle(key)} className="flex items-start gap-2 text-left flex-1">
                    {on ? <Check size={16} className="text-brand mt-0.5"/> : <Circle size={16} className="text-[color:var(--aii-muted)] mt-0.5"/>}
                    <span className={`text-sm ${on ? "font-medium" : ""}`}>{m.label}</span>
                  </button>
                  <div className="flex items-center gap-1">
                    {!isEditing && (
                      <button data-testid={`wa-edit-${m.key}`} onClick={()=>{ setEditingKey(m.key); setDraftTemplate(m.template); }}
                        className="text-xs flex items-center gap-1 px-2 py-1 rounded border border-[color:var(--aii-border)] bg-white hover:bg-[color:var(--aii-green-tint)]" title="Edit template">
                        <Pencil size={11} />
                      </button>
                    )}
                    <button data-testid={`wa-copy-${m.key}`} onClick={()=>copyToClipboard(m.template)}
                      className="text-xs flex items-center gap-1 px-2 py-1 rounded border border-[color:var(--aii-border)] bg-white hover:bg-[color:var(--aii-green-tint)]">
                      <Copy size={11} /> Copy
                    </button>
                  </div>
                </div>
                {isEditing ? (
                  <div>
                    <textarea data-testid={`wa-textarea-${m.key}`} value={draftTemplate} onChange={e=>setDraftTemplate(e.target.value)}
                      rows={6}
                      className="w-full text-xs bg-white p-2 rounded border border-[color:var(--aii-border)] font-mono whitespace-pre-line" />
                    <div className="flex justify-end gap-1 mt-2 flex-wrap">
                      <button onClick={()=>{ setEditingKey(null); setDraftTemplate(""); }} className="text-xs px-3 py-1 rounded border border-[color:var(--aii-border)] bg-white">Cancel</button>
                      <button data-testid={`wa-save-global-${m.key}`} onClick={()=>saveTemplate(m.key, "global")}
                        className="text-xs flex items-center gap-1 px-3 py-1 rounded border border-brand text-brand hover:bg-[color:var(--aii-green-tint)] font-semibold" title="Save as global default (used everywhere no override exists)">
                        <Save size={11} /> Save global
                      </button>
                      {country && (
                        <button data-testid={`wa-save-local-${m.key}`} onClick={()=>saveTemplate(m.key, "local")}
                          className="text-xs flex items-center gap-1 px-3 py-1 rounded bg-brand text-white hover:bg-brand-dark font-semibold" title={`Save only for ${country}`}>
                          <Save size={11} /> Localise for {country}
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-[color:var(--aii-text-2)] whitespace-pre-line bg-white/50 p-2 rounded border border-[color:var(--aii-border)]">
                    {m.template}
                    {m.country && <div className="mt-1 text-[9px] uppercase tracking-widest text-brand font-bold flex items-center gap-1"><Pencil size={8}/> localised for {m.country}</div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="aii-card p-6">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="font-semibold">Daily checklist — {new Date(logDate).toLocaleDateString(undefined,{weekday:"long", month:"long", day:"numeric"})}</h3>
          <div className="flex items-center gap-3">
            <div className="text-sm text-[color:var(--aii-text-2)]">{done}/{allItems.length} done</div>
            {/* Move to My Day — turns every checklist item into a
                daily-recurring personal task so GEHM ticks them off in the
                normal Tasks flow instead of the standalone checklist. */}
            <button data-testid="hk-import-tasks"
              onClick={async () => {
                try {
                  const { data } = await api.post("/tasks/import-housekeeping-checklist");
                  if (data.created > 0) toast.success(`Moved ${data.created} item(s) to My Day as daily tasks`);
                  else toast.info(data.message || "All items already imported");
                } catch (e) { toast.error(e?.response?.data?.detail || "Move failed"); }
              }}
              className="text-xs px-3 py-1.5 border border-brand text-brand rounded-md hover:bg-[color:var(--aii-green-tint)] font-semibold">
              Move checklist to My Day
            </button>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-2">
          {allItems.map(label => {
            const on = !!log?.items?.[label];
            const extraObj = extras.find(x=>x.name===label);
            return (
              <div key={label} className="flex items-center gap-2">
                <button onClick={()=>toggle(label)}
                  className={`flex-1 flex items-start gap-3 p-3 rounded-lg border text-left transition ${on ? 'border-brand bg-[color:var(--aii-green-tint)]' : 'border-[color:var(--aii-border)] bg-white hover:border-brand'}`}>
                  {on ? <Check size={18} className="text-brand mt-0.5"/> : <Circle size={18} className="text-[color:var(--aii-muted)] mt-0.5"/>}
                  <span className={`text-sm ${on ? 'font-medium' : ''}`}>{label}</span>
                </button>
                {extraObj && (
                  <button onClick={()=>rmExtra(extraObj.id)} className="text-[color:var(--aii-muted)] hover:text-red-600" title="Remove custom task"><X size={14}/></button>
                )}
              </div>
            );
          })}
        </div>
        <div className="mt-4 flex gap-2 items-center pt-3 border-t border-[color:var(--aii-border)]">
          <input value={addingTask} onChange={e=>setAddingTask(e.target.value)} onKeyDown={e=>{if(e.key==='Enter') addTask();}}
            placeholder="Add a new recurring task…" className="flex-1 px-3 py-2 border border-[color:var(--aii-border)] rounded bg-white text-sm"/>
          <button onClick={addTask} className="flex items-center gap-1 text-sm bg-brand text-white px-3 py-2 rounded hover:bg-brand-dark font-semibold"><Plus size={14}/> Add task</button>
        </div>
      </div>

      <div className="aii-card p-6 space-y-3">
        <div className="flex items-center gap-2">
          <input id="cook_absent" type="checkbox" checked={!!log?.cook_absent} onChange={e=>setLog(v=>({...v, cook_absent: e.target.checked}))}/>
          <label htmlFor="cook_absent" className="text-sm font-semibold">Cook absent — external meals ordered</label>
        </div>
        {/* Online order amount is now always accessible so the housekeeping
            head can log an ad-hoc food expense even when the cook is present
            (Sunday cheat meal, late arrivals, guest lunches).  When cook is
            marked absent, we also surface the "Meals ordered" note. */}
        <div className="grid sm:grid-cols-2 gap-3">
          {log?.cook_absent && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--aii-text-2)]">Meals ordered (what & how many)</label>
              <input value={log?.cook_absent_meals || ""} onChange={e=>setLog(v=>({...v, cook_absent_meals: e.target.value}))} placeholder="e.g. 8× thali from Rawat Misthan" className="input-aii"/>
            </div>
          )}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--aii-text-2)]">Online order amount (INR)</label>
            <input type="number" data-testid="hk-order-amount" value={log?.budget_requested || 0}
                   onChange={e=>setLog(v=>({...v, budget_requested: e.target.value}))}
                   placeholder="e.g. 1450"
                   className="input-aii"/>
          </div>
        </div>
      </div>

      {/* Submit — writes the checklist + cook absence + online order into
          /housekeeping/logs.  Wasn't in the UI earlier, so users had no way
          to persist their entries. */}
      <div className="flex justify-end">
        <button data-testid="hk-submit" onClick={save}
                className="bg-brand text-white font-semibold px-6 py-2.5 rounded-md hover:bg-brand-dark shadow-sm">
          Submit today&apos;s housekeeping log
        </button>
      </div>
    </div>
  );
}
